import { useNavigate } from 'react-router-dom';
import { useTransition } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addToCart,
  getExistingCartItemById,
  setSelectedPaymentPlan,
} from './cartSlice';
import { toast } from 'react-toastify';
import { trackAddToCart } from '../../lib/eventTracker';

export const handleAddToCart = (
  dispatch,
  product,
  paymentOptions,
  selectedPaymentPlan,
  existing
) => {
  if (!product) return;

  const {
    productID,
    productName,
    coverImage,
    fairAppPrice,
    quantity = 1,
  } = product;

  //Mixpanel track add to cart
  trackAddToCart(product);

  // Map payment options for easy access
  const paymentMap = {};
  paymentOptions.forEach((option) => {
    if (option.type) paymentMap[option.type.toLowerCase()] = option;
  });

  //  Default to 'monthly' if no selected plan
  const activePaymentPlan = selectedPaymentPlan || 'monthly';
  const newItem = {
    productID,
    productName,
    coverImage,
    quantity,
    price: fairAppPrice,
    totalPrice: fairAppPrice * quantity,
    paymentPlan: activePaymentPlan,
    selectedPaymentPlan: activePaymentPlan,
    paymentOptions: [
      {
        type: 'full',
        amount: paymentMap.full?.amount || fairAppPrice,
        totalPrice: paymentMap.full?.totalPrice || fairAppPrice,
      },
      {
        type: 'monthly',
        months: paymentMap.monthly?.months || 0,
        monthlyPayment: paymentMap.monthly?.monthlyPayment || 0,
        downPayment: paymentMap.monthly?.downPayment || 0,
        totalPrice: paymentMap.monthly?.totalPrice || fairAppPrice,
      },
      {
        type: 'weekly',
        weeks: paymentMap.weekly?.weeks || 0,
        weeklyPayment: paymentMap.weekly?.weeklyPayment || 0,
        downPayment: paymentMap.weekly?.downPayment || 0,
        totalPrice: paymentMap.weekly?.totalPrice || fairAppPrice,
      },
      {
        type: 'daily',
        days: paymentMap.daily?.days || 0,
        dailyPayment: paymentMap.daily?.dailyPayment || 0,
        downPayment: paymentMap.daily?.downPayment || 0,
        totalPrice: paymentMap.daily?.totalPrice || fairAppPrice,
      },
    ],
  };

  // Dispatch the addToCart action
  dispatch(addToCart(newItem));

  if (existing) {
    toast.success('Item quantity updated in cart', {
      className: 'bg-[#ffde11] text-black text-sm px-1 py-1 rounded-md min-h-0',
    });
  } else {
    toast.success(
      <div className="flex items-center space-x-2 justify-between h-[42px]">
        <div className="flex space-x-1 items-center">
          <span className="font-light text-base">Item added to cart</span>
          <img src="/images/blackcheckcircle.svg" alt="icon" />
        </div>
      </div>,
      {
        icon: false,
        type: 'success',
        className: 'toast-yellow',
        bodyClassName: 'm-0 p-0',
        closeButton: false,
      }
    );
  }
};
export const AddToCart = ({ product }) => {
  const existing = useSelector(getExistingCartItemById(product?.productID));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();

  const handleAddToCart = async () => {
    if (!product) return;

    const options = Array.isArray(product.paymentOptionsBreakdown)
      ? product.paymentOptionsBreakdown
      : JSON.parse(product.paymentOptionsBreakdown || '[]');

    let planToUse = 'monthly';
    if (options.length === 1 && options[0]?.type?.toLowerCase() === 'full') {
      planToUse = 'full';
    }

    // Prevent duplicates
    if (existing) {
      toast.warn('Item already in cart', {
        className:
          'bg-[var(--yellow-primary)] text-black text-sm px-1 py-1 rounded-md min-h-0',
      });
      return;
    }

    try {
      //  Attach the plan directly to product payload
      const productWithPlan = { ...product, selectedPaymentPlan: planToUse };

      await dispatch(addToCart(productWithPlan)).unwrap();

      startTransition(() => {
        toast.success(
          <div className="flex items-center space-x-2 justify-between h-[42px]">
            <div className="flex space-x-1 items-center">
              <span className="font-light text-base">Item added to cart</span>
              <img src="/images/blackcheckcircle.svg" alt="icon" />
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-black">|</span>
              <button
                onClick={() => navigate('/cart-items')}
                className="underline text-base font-medium"
              >
                View cart
              </button>
            </div>
          </div>,
          {
            icon: false,
            type: 'success',
            className: 'toast-yellow',
            bodyClassName: 'm-0 p-0',
            closeButton: false,
          }
        );
      });
    } catch (error) {
      toast.error(error || 'Failed to add item to cart', {
        className:
          'bg-[var(--yellow-primary)] text-black text-sm px-1 py-1 rounded-md min-h-0',
        bodyClassName: 'm-0 p-0',
        closeButton: false,
      });
    }
  };

  return (
    <button
      className="group bg-[var(--yellow-primary)] flex items-center rounded-[20px] px-2 py-2 overflow-hidden transition-all duration-300"
      onClick={handleAddToCart}
      disabled={isPending}
    >
      <div className="flex items-center transition-all duration-300 max-w-[22px] group-hover:max-w-[120px]">
        <img
          src="/images/shopping-bag-add.svg"
          alt="Add to shopping cart"
          className="w-[18px] lg:w-5 shrink-0"
        />
        <span className="ml-2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          {isPending ? 'Adding...' : 'Add to cart'}
        </span>
      </div>
    </button>
  );
};
